import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import Modal from '~/app/components/Modal';
import Button from '~/app/ui/Button';
import Segmented from '~/app/ui/Segmented';
import EmptyState from '~/app/ui/EmptyState';
import StatusBadge from '~/app/ui/StatusBadge';
import { S, A } from '~/app/ui/tokens';
import { buildWidgetSrcDoc } from './widgetPreviewDoc';

export const WIDGET_BODY = gql`
  query getCustomWidgetBody($name: String!, $version: String) {
    customWidgetForRender(name: $name, version: $version) {
      versionId
      html
      css
      js
      defaultProps
      scoper
      propsSchema {
        key
        label
        type
        defaultValue
        children {
          key
          label
          type
          defaultValue
        }
      }
    }
  }
`;

export type WidgetSummary = {
  _id: string;
  name: string;
  title: string;
  description?: string | null;
  category?: string | null;
  publishedVersion?: string | null;
  isActive: boolean;
};

type Field = { key: string; label?: string; type?: string; defaultValue?: unknown; children?: Field[] };
type Tab = 'preview' | 'props' | 'html' | 'css' | 'js';

const useStyles = createUseStyles({
  root: { display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0, fontFamily: S.ui, color: S.fg },
  bar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 20px', borderBottom: `1px solid ${S.line}` },
  meta: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, fontSize: 13, color: S.muted },
  name: { fontFamily: S.mono, fontSize: 12 },
  dark: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.muted, cursor: 'pointer' },
  body: { flex: 1, minHeight: 0, overflow: 'auto', background: S.chrome },
  frame: { width: '100%', height: '100%', minHeight: 420, border: 0, background: '#fff', display: 'block' },
  fields: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, padding: 20 },
  field: { padding: '12px 14px', background: A.surface, border: `1px solid ${S.line}`, borderRadius: 10, display: 'grid', gap: 4, fontSize: 13 },
  key: { fontFamily: S.mono, fontSize: 12 },
  type: { justifySelf: 'start', padding: '1px 6px', borderRadius: 4, background: S.chromeHover, fontSize: 11, fontWeight: 600 },
  value: { fontFamily: S.mono, fontSize: 12, color: S.muted, wordBreak: 'break-all' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: `1px solid ${S.line}` }
});

/** A widget up close: how it looks with its default settings, its settings, and its code. */
const WidgetDetails: React.FC<{ widget: WidgetSummary; onClose: () => void; onOpen: () => void }> = ({ widget, onClose, onOpen }) => {
  const c = useStyles();
  const [tab, setTab] = useState<Tab>('preview');
  const [dark, setDark] = useState(false);
  // The published version is part of the variables, so a newly published version isn't served from the cache.
  const { data, loading, error } = useQuery(WIDGET_BODY, { variables: { name: widget.name, version: widget.publishedVersion || null }, skip: !widget.isActive });
  const body = data?.customWidgetForRender;
  const fields: Field[] = body?.propsSchema || [];

  const code = (mode: 'html' | 'css' | 'javascript', value: string, empty: string) => (
    <AceEditor mode={mode} theme="monokai" name={`widget-${mode}`} readOnly value={value || empty} width="100%" height="100%" fontSize={13} showPrintMargin={false} editorProps={{ $blockScrolling: true }} />
  );

  let content: React.ReactNode;
  if (!widget.isActive) content = <EmptyState icon="delete_outline" title="This widget is deleted">Restore it to preview it.</EmptyState>;
  else if (error) content = <EmptyState tone="error" title="Couldn’t load the widget">{error.message}</EmptyState>;
  else if (loading) content = <EmptyState icon="hourglass_empty" title="Loading…" compact />;
  else if (!body) content = <EmptyState icon="widgets" title="Nothing to show yet">This widget has no saved version.</EmptyState>;
  else if (tab === 'preview') {
    content = <iframe className={c.frame} title={`${widget.title} preview`} sandbox="allow-scripts" srcDoc={buildWidgetSrcDoc(body, `details-${widget.name}`, { padding: 24, dark })} />;
  } else if (tab === 'props') {
    content = fields.length === 0 ? <EmptyState icon="tune" title="No settings">This widget has nothing to set in the editor.</EmptyState> : (
      <div className={c.fields}>
        {fields.map(f => (
          <div key={f.key} className={c.field}>
            <b>{f.label || f.key}</b>
            <span className={c.key}>{`{{${f.key}}}`}</span>
            {f.type && <span className={c.type}>{f.type}</span>}
            {f.defaultValue !== undefined && f.defaultValue !== null && <span className={c.value}>Default: {JSON.stringify(f.defaultValue)}</span>}
            {f.children && f.children.length > 0 && <span className={c.value}>Fields: {f.children.map(x => x.key).join(', ')}</span>}
          </div>
        ))}
      </div>
    );
  } else if (tab === 'html') content = code('html', body.html, '<!-- No HTML -->');
  else if (tab === 'css') content = code('css', body.css, '/* No CSS */');
  else content = code('javascript', body.js, '// No JavaScript');

  return (
    // Its buttons are in its own footer row: no empty dialog footer under them.
    <Modal title={widget.title} onClose={onClose} disableFooter>
      <div className={c.root} data-testid="widget-details">
        <div className={c.bar}>
          <div className={c.meta}>
            {widget.publishedVersion ? <StatusBadge tone="green" label={`Published v${widget.publishedVersion}`} /> : <StatusBadge tone="gray" label="Not published" />}
            <span className={c.name}>Custom_{widget.name}</span>
            {widget.description && <span>{widget.description}</span>}
          </div>
          <Segmented<Tab>
            label="Show"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'preview', label: 'Preview' },
              { value: 'props', label: `Settings${fields.length ? ` (${fields.length})` : ''}` },
              { value: 'html', label: 'HTML' },
              { value: 'css', label: 'CSS' },
              { value: 'js', label: 'JS' }
            ]}
          />
          {tab === 'preview' && (
            <label className={c.dark}>
              <input type="checkbox" checked={dark} onChange={e => setDark(e.target.checked)} />
              Dark background
            </label>
          )}
        </div>
        <div className={c.body}>{content}</div>
        <div className={c.foot}>
          <Button onClick={onClose}>Close</Button>
          {widget.isActive && <Button variant="primary" icon="edit" onClick={onOpen}>Open in builder</Button>}
        </div>
      </div>
    </Modal>
  );
};

export default WidgetDetails;
